/**
 * ReceiptSummary Component
 * Displays the receipt of items to be received
 */

import type { ReceivedItem } from '@/types/index.ts';
import { requiresItemsPerUnit } from '@/types/index.ts';

interface ReceiptSummaryProps {
  items: ReceivedItem[];
  onRemoveItem: (index: number) => void;
  onSubmit: () => void;
  notes: string;
  onNotesChange: (value: string) => void;
  isSubmitting: boolean;
}

export default function ReceiptSummary({
  items,
  onRemoveItem,
  onSubmit,
  notes,
  onNotesChange,
  isSubmitting,
}: ReceiptSummaryProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Receipt Summary</h3>
        <p className="text-sm text-gray-600 mt-1">{items.length} item(s) to receive</p>
      </div>

      <div className="p-6">
        {/* Receipt Items */}
        <div className="space-y-3 mb-4">
          {items.map((item, index) => {
            const needsItemsPerUnit = requiresItemsPerUnit(item.unit);
            
            return (
              <div
                key={index}
                className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.name}</p>
                      <div className="mt-1 space-y-1">
                        {needsItemsPerUnit && item.packsBoxes && item.itemsPerUnit ? (
                          <>
                            <p className="text-xs text-gray-600">
                              {item.packsBoxes} {item.unit}
                            </p>
                            <p className="text-xs text-gray-600">
                              {item.itemsPerUnit} items per {item.unit}
                            </p>
                            <p className="text-xs font-medium text-gray-900">
                              Total: {item.quantity} items
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-gray-600">
                            {item.quantity} {item.unit}
                          </p>
                        )}
                        {item.location && (
                          <p className="text-xs text-gray-500">Location: {item.location}</p>
                        )}
                        {item.category && (
                          <p className="text-xs text-gray-500">Category: {item.category}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Batch: <span className="font-medium text-gray-700">{item.batch_number}</span>
                        </p>
                        {item.expiry_date && (
                          <p className="text-xs text-gray-500">
                            Expires: <span className="font-medium text-gray-700">{
                              // Parse as local date to avoid UTC-offset day shift
                              (() => {
                                const [y, m, d] = item.expiry_date!.split('-').map(Number);
                                return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                              })()
                            }</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveItem(index)}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                      title="Remove item"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Add any notes about this receipt..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] resize-none"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="w-full px-4 py-3 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-check-line"></i>
              Receive Stock
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

